// src/pages/Platform/Seats.tsx — purchased seats per organisation.
//
// Green Giraffe only (2026-10-14). Seats are what a customer buys; agent
// extensions and the browser softphone are both counted against them. Master
// admins see this in the sidebar; any admin of the platform root organisation
// may also open it (the server decides — a provider gets 403 and this page
// shows that message instead of the table).
import React, { useEffect, useMemo, useState } from 'react';
import { Armchair, Pencil, Plus, RefreshCw, ShieldAlert } from 'lucide-react';
import {
  seatAllocationsService, SeatOverviewRow, errorMessage,
} from 'api/services/seatAllocations.service';
import { draftFor, statusOf, validateDraft, isAutoRange, SeatDraft } from './seatAllocationRules';

const STATUS_STYLE: Record<string, string> = {
  Active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Full: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Disabled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  'No seats': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

const Seats: React.FC = () => {
  const [rows, setRows] = useState<SeatOverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<SeatDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await seatAllocationsService.overview());
    } catch (e: any) {
      if (e?.response?.status === 403) setForbidden(errorMessage(e));
      else setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const usedFor = (orgId: string) => rows.find(r => r.organizationId === orgId)?.usedSeats ?? 0;
  const problem = draft ? validateDraft(draft, usedFor(draft.organizationId)) : null;

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? rows.filter(r => r.organizationName?.toLowerCase().includes(q)) : rows;
  }, [rows, filter]);

  const save = async () => {
    if (!draft || problem) return;
    setSaving(true);
    setSaveError(null);
    try {
      await seatAllocationsService.grant({
        organizationId: draft.organizationId,
        ...(isAutoRange(draft) ? {} : { rangeStart: Number(draft.rangeStart), rangeEnd: Number(draft.rangeEnd) }),
        maxSeats: Number(draft.maxSeats),
        enabled: draft.enabled,
        notes: draft.notes || undefined,
      });
      setDraft(null);
      await load();
    } catch (e: any) {
      // The server's validations (range overlap with existing seats, shrink
      // below use, GG-only) arrive verbatim.
      setSaveError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (forbidden) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Restricted</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{forbidden}</p>
        </div>
      </div>
    );
  }

  const input = 'w-full rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white';

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Armchair className="w-6 h-6 text-blue-600" /> Seats
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Purchased seats per organisation. A seat is an agent extension, or a browser softphone for
            someone without one. Without an enabled allocation an organisation gets no seats and no softphone.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => void load()}
            className="inline-flex items-center gap-1.5 rounded border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button onClick={() => { setSaveError(null); setDraft(draftFor(null)); }}
            className="inline-flex items-center gap-1.5 rounded bg-blue-600 px-2.5 py-1.5 text-sm text-white hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Grant seats
          </button>
        </div>
      </div>

      {error && <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">{error}</div>}

      {draft && (
        <div className="rounded-lg border border-blue-200 bg-white p-4 shadow-sm dark:border-blue-800 dark:bg-gray-800 space-y-3" data-testid="seat-draft">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <label className="text-xs text-gray-600 dark:text-gray-300 lg:col-span-2">Organisation
              <select className={input} value={draft.organizationId}
                onChange={e => setDraft({ ...draft, organizationId: e.target.value })}>
                <option value="">Choose…</option>
                {rows.map(r => <option key={r.organizationId} value={r.organizationId}>{r.organizationName}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600 dark:text-gray-300">Range start
              <input className={input} inputMode="numeric" placeholder="Automatic" value={draft.rangeStart}
                onChange={e => setDraft({ ...draft, rangeStart: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600 dark:text-gray-300">Range end
              <input className={input} inputMode="numeric" placeholder="Automatic" value={draft.rangeEnd}
                onChange={e => setDraft({ ...draft, rangeEnd: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600 dark:text-gray-300">Max seats
              <input className={input} inputMode="numeric" value={draft.maxSeats}
                onChange={e => setDraft({ ...draft, maxSeats: e.target.value })} />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <label className="text-xs text-gray-600 dark:text-gray-300">Notes
              <input className={input} value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} />
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input type="checkbox" checked={draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked })} />
              Enabled
            </label>
          </div>
          {(problem || saveError) && (
            <p className="text-sm text-red-600 dark:text-red-400">{saveError || problem}</p>
          )}
          <div className="flex gap-2">
            <button disabled={!!problem || saving} onClick={() => void save()}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50 hover:bg-blue-700">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setDraft(null)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-200">
              Cancel
            </button>
          </div>
        </div>
      )}

      <input className={`${input} max-w-xs`} placeholder="Filter organisations…" value={filter}
        onChange={e => setFilter(e.target.value)} />

      <div className="overflow-x-auto rounded-lg bg-white shadow dark:bg-gray-800">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-900 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2">Organisation</th>
              <th className="px-3 py-2">Range</th>
              <th className="px-3 py-2">Seats used / max</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading && <tr><td colSpan={5} className="px-3 py-4 text-gray-500">Loading…</td></tr>}
            {!loading && visible.map(r => {
              const status = statusOf(r);
              return (
                <tr key={r.organizationId} className="text-gray-800 dark:text-gray-100">
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.organizationName}</div>
                    <div className="text-xs text-gray-500">{r.organizationType?.toLowerCase()}</div>
                  </td>
                  <td className="px-3 py-2 font-mono">{r.allocation ? `${r.allocation.rangeStart}–${r.allocation.rangeEnd}` : '—'}</td>
                  <td className="px-3 py-2">{r.allocation ? `${r.usedSeats} / ${r.allocation.maxSeats}` : (r.usedSeats ? `${r.usedSeats} / —` : '—')}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[status] || 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`}>{status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => { setSaveError(null); setDraft(draftFor(r)); }}
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400">
                      <Pencil className="h-3.5 w-3.5" /> {r.allocation ? 'Edit' : 'Grant'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Seats;
